import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { useNotification } from '../context/NotificationContext';
import { AdminWithdrawal, AdminWithdrawalsService, WithdrawalStatus } from '../services/admin-withdrawals.service';
import styles from './AdminWithdrawalsPage.module.css';
import { useAuth } from '../context/AuthContext';

const statusFilters: WithdrawalStatus[] = ['PENDING', 'APPROVED', 'EXECUTED', 'REJECTED'];

export default function AdminWithdrawalsPage() {
  const { t } = useTranslation();
  const { addNotification } = useNotification();
  const { user } = useAuth();

  if (user?.role === 'manager') {
    return <Navigate to="/admin/dashboard" replace />;
  }

  const [items, setItems] = useState<AdminWithdrawal[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<WithdrawalStatus>('PENDING');
  const [selected, setSelected] = useState<AdminWithdrawal | null>(null);
  const [mode, setMode] = useState<'review' | 'execute' | 'view' | null>(null);
  const [notes, setNotes] = useState('');
  const [reference, setReference] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await AdminWithdrawalsService.list(filter);
      setItems(Array.isArray(res) ? res : []);
    } catch (error: any) {
      addNotification(error?.message || t('common.error_generic'), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const openModal = (item: AdminWithdrawal, nextMode: 'review' | 'execute' | 'view') => {
    setSelected(item);
    setMode(nextMode);
    setNotes('');
    setReference('');
  };

  const closeModal = () => {
    if (submitting) return;
    setSelected(null);
    setMode(null);
    setNotes('');
    setReference('');
  };

  const handleApprove = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      await AdminWithdrawalsService.approve(selected.id);
      addNotification(t('admin.withdrawals_approve_success'), 'success');
      closeModal();
      await load();
    } catch (error: any) {
      addNotification(error?.message || t('common.error_generic'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      await AdminWithdrawalsService.reject(selected.id, notes.trim() || undefined);
      addNotification(t('admin.withdrawals_reject_success'), 'success');
      closeModal();
      await load();
    } catch (error: any) {
      addNotification(error?.message || t('common.error_generic'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleExecute = async () => {
    if (!selected) return;
    if (!reference.trim()) {
      addNotification(t('admin.withdrawals_reference_required'), 'warning');
      return;
    }

    setSubmitting(true);
    try {
      await AdminWithdrawalsService.execute(selected.id, {
        reference: reference.trim(),
        notes: notes.trim() || undefined
      });
      addNotification(t('admin.withdrawals_execute_success'), 'success');
      closeModal();
      await load();
    } catch (error: any) {
      addNotification(error?.message || t('common.error_generic'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const statusLabel = (status: WithdrawalStatus) => {
    const map: Record<WithdrawalStatus, string> = {
      PENDING: t('admin.withdrawals_status_pending'),
      APPROVED: t('admin.withdrawals_status_approved'),
      EXECUTED: t('admin.withdrawals_status_executed'),
      REJECTED: t('admin.withdrawals_status_rejected')
    };
    return map[status] || status;
  };

  const statusClass = (status: WithdrawalStatus) => {
    const map: Record<WithdrawalStatus, string> = {
      PENDING: styles.badgePending,
      APPROVED: styles.badgeApproved,
      EXECUTED: styles.badgeExecuted,
      REJECTED: styles.badgeRejected
    };
    return map[status] || '';
  };

  const selectedUserName = useMemo(() => {
    if (!selected?.user) return '-';
    return `${selected.user.first_name ?? ''} ${selected.user.last_name ?? ''}`.trim() || '-';
  }, [selected]);

  const executionTimestamp = useMemo(() => {
    if (mode !== 'execute') return '';
    return new Date().toLocaleString('es-MX');
  }, [mode, selected]);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>{t('admin.withdrawals_title')}</h1>
          <p className={styles.subtitle}>{t('admin.withdrawals_subtitle')}</p>
        </div>
        <button
          type="button"
          className={styles.refreshButton}
          onClick={() => load()}
          disabled={loading}
        >
          {t('common.refresh') || 'Actualizar'}
        </button>
      </header>

      <div className={styles.filters}>
        {statusFilters.map((status) => (
          <button
            key={status}
            className={filter === status ? styles.filterActive : styles.filterButton}
            onClick={() => setFilter(status)}
          >
            {statusLabel(status)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className={styles.loading}>{t('common.loading')}</div>
      ) : items.length === 0 ? (
        <div className={styles.empty}>{t('admin.withdrawals_empty')}</div>
      ) : (
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>ID</th>
                <th>{t('admin.withdrawals_table_date')}</th>
                <th>{t('admin.withdrawals_table_user')}</th>
                <th>{t('admin.withdrawals_table_amount')}</th>
                <th>{t('admin.withdrawals_table_status')}</th>
                <th>{t('admin.withdrawals_table_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const actionLabel =
                  item.status === 'PENDING'
                    ? t('admin.withdrawals_action_review')
                    : item.status === 'APPROVED'
                      ? t('admin.withdrawals_action_execute')
                      : t('admin.withdrawals_action_view');

                const actionMode =
                  item.status === 'PENDING'
                    ? 'review'
                    : item.status === 'APPROVED'
                      ? 'execute'
                      : 'view';

                return (
                  <tr key={item.id}>
                    <td className={styles.idColumn}>#{item.id}</td>
                    <td>{new Date(item.requested_at).toLocaleDateString('es-MX')}</td>
                    <td>
                      {item.user
                        ? `${item.user.first_name ?? ''} ${item.user.last_name ?? ''}`.trim() || '—'
                        : '—'}
                      <br />
                      <small className={styles.email}>{item.user?.email}</small>
                    </td>
                    <td className={styles.amount}>
                      ${Number(item.amount).toLocaleString('es-MX')} {item.currency}
                    </td>
                    <td>
                      <span className={`${styles.badge} ${statusClass(item.status)}`}>
                        {statusLabel(item.status)}
                      </span>
                    </td>
                    <td>
                      <button
                        className={styles.actionButton}
                        onClick={() => openModal(item, actionMode)}
                      >
                        {actionLabel}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected && mode ? (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modal} onClick={(event) => event.stopPropagation()}>
            <button className={styles.closeButton} onClick={closeModal} disabled={submitting}>
              ✕
            </button>
            <h2 className={styles.modalTitle}>{t('admin.withdrawals_modal_title')}</h2>

            <div className={styles.modalBody}>
              <div className={styles.detailGrid}>
                <div>
                  <span className={styles.detailLabel}>{t('admin.withdrawals_table_user')}</span>
                  <div className={styles.detailValue}>{selectedUserName}</div>
                  <div className={styles.muted}>{selected.user?.email}</div>
                </div>
                <div>
                  <span className={styles.detailLabel}>{t('admin.withdrawals_project') || 'Proyecto'}</span>
                  <div className={styles.detailValue}>{selected.project?.name ?? '—'}</div>
                  <div className={styles.muted}>{selected.project?.slug ?? ''}</div>
                </div>
                <div>
                  <span className={styles.detailLabel}>{t('admin.withdrawals_table_amount')}</span>
                  <div className={styles.detailValue}>
                    ${Number(selected.amount).toLocaleString('es-MX')} {selected.currency}
                  </div>
                </div>
                <div>
                  <span className={styles.detailLabel}>{t('admin.withdrawals_requested_at') || 'Fecha solicitud'}</span>
                  <div className={styles.detailValue}>
                    {new Date(selected.requested_at).toLocaleString('es-MX')}
                  </div>
                </div>
                <div>
                  <span className={styles.detailLabel}>{t('admin.withdrawals_wallet_available')}</span>
                  <div className={styles.detailValue}>
                    ${Number(selected.wallet?.available ?? 0).toLocaleString('es-MX')} {selected.wallet?.currency ?? 'MXN'}
                  </div>
                </div>
                <div>
                  <span className={styles.detailLabel}>{t('admin.withdrawals_bank_clabe')}</span>
                  <div className={styles.detailValue}>
                    {selected.bankAccount?.clabe ?? selected.bankAccount?.clabeMasked ?? '—'}
                  </div>
                  <div className={styles.muted}>{selected.bankAccount?.bank?.name ?? ''}</div>
                </div>
              </div>

              {selected.user_comment ? (
                <div className={styles.commentBox}>
                  <span className={styles.detailLabel}>{t('admin.withdrawals_user_comment')}</span>
                  <div>{selected.user_comment}</div>
                </div>
              ) : null}

              {(mode === 'review' || mode === 'execute') && (
                <div className={styles.formSection}>
                  {mode === 'execute' ? (
                    <div className={styles.field}>
                      <label className={styles.label}>{t('admin.withdrawals_reference_label')}</label>
                      <input
                        className={styles.input}
                        value={reference}
                        onChange={(e) => setReference(e.target.value)}
                      />
                    </div>
                  ) : null}
                  {mode === 'execute' ? (
                    <div className={styles.notice}>
                      {t('admin.withdrawals_execute_time_note', {
                        date: executionTimestamp
                      }) || `La ejecución se registrará con la fecha/hora actual: ${executionTimestamp}`}
                    </div>
                  ) : null}

                  <div className={styles.field}>
                    <label className={styles.label}>{t('admin.withdrawals_notes_label')}</label>
                    <textarea
                      className={styles.textarea}
                      rows={3}
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className={styles.modalFooter}>
              <button className={styles.ghostButton} onClick={closeModal} disabled={submitting}>
                {t('common.back')}
              </button>
              {mode === 'review' ? (
                <div className={styles.footerActions}>
                  <button className={styles.rejectButton} onClick={handleReject} disabled={submitting}>
                    {t('admin.withdrawals_reject_action')}
                  </button>
                  <button className={styles.primaryButton} onClick={handleApprove} disabled={submitting}>
                    {t('admin.withdrawals_approve_action')}
                  </button>
                </div>
              ) : null}
              {mode === 'execute' ? (
                <button className={styles.primaryButton} onClick={handleExecute} disabled={submitting}>
                  {t('admin.withdrawals_execute_action')}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
